import React, { useCallback, useEffect, useReducer } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import ReactModal from "react-modal";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import moment from "moment-timezone";
import { useDebouncedCallback } from "use-debounce";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";

import ai from "../../helpers/axios";
import getApiErrorMessage from "../../helpers/get-api-error-message";
import { formatTripDateRange } from "../../helpers/dates";
import { CLIENT_PAYMENT_TYPE_OPTIONS } from "../../helpers/format-payment-type";
import formatSignedAmount from "../../helpers/format-signed-amount";
import { LoadingButton } from "../shared/loading-button";
import TextAreaInput from "../textarea-input";
import { buildLinkNotes, formatProspectOption, getDefaultLinkPaymentType } from "./helpers";

const DEFAULT_OPTIONS_MAX_LIMIT = 50;

const LINK_KINDS = {
  passthrough: {
    title: "Link Payment to Passthrough Payments",
    endpoint: "link-passthrough",
    counterpartLabel: "Operator",
    counterpartEditable: false,
    success: "Payment linked to passthrough payments successfully.",
    failure: "Error linking payment to passthrough payments.",
  },
  referralFee: {
    title: "Link Payment to a Referral Fee",
    endpoint: "link-referral-fee",
    counterpartLabel: "Paid To",
    counterpartEditable: true,
    success: "Payment linked to referral fees successfully.",
    failure: "Error linking payment to a referral fee.",
  },
};

const getCounterpart = (row) => (row?.objectType ? row?.cardholderName : row?.contactName) || "";

const formatTripOptionWithClient = ({ _id, tripTag, firstDepartureDate, lastArrivalDate, routing, clientName }) => ({
  value: _id,
  label: [
    tripTag,
    formatTripDateRange(firstDepartureDate, lastArrivalDate),
    clientName,
    routing ?? "-",
  ].filter(Boolean).join(" — "),
});

const buildInitialState = (row) => {
  const defaultPaymentType = row ? getDefaultLinkPaymentType(row) : null;

  return {
    isSaving: false,
    prospectOption: null,
    tripOption: null,
    paymentType: CLIENT_PAYMENT_TYPE_OPTIONS.find(({ value }) => value === defaultPaymentType) || null,
    counterpart: getCounterpart(row),
    notes: row ? buildLinkNotes(row) : "",
  };
};

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged":
      return { ...state, [action.field]: action.value };
    case "reset":
      return buildInitialState(action.row);
    default:
      return state;
  }
};

const TripRecordLinkModal = ({ isOpen, row, onRequestClose, kind = "passthrough" }) => {
  const config = LINK_KINDS[kind];
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [state, dispatch] = useReducer(reducer, buildInitialState(row));

  useEffect(() => {
    if (isOpen) {
      dispatch({ type: "reset", row });
    }
  }, [isOpen, row]);

  const getToken = useCallback(
    () => getAccessTokenSilently().catch(() => loginWithRedirect()),
    [getAccessTokenSilently, loginWithRedirect]
  );

  const handleClose = useCallback(() => {
    onRequestClose(false);
  }, [onRequestClose]);

  const debouncedLoadProspects = useDebouncedCallback((inputValue, callback) => {
    getToken()
      .then((token) =>
        ai.auth(token).get("/api/prospects/search", {
          params: { q: inputValue, limit: DEFAULT_OPTIONS_MAX_LIMIT },
        })
      )
      .then(({ data }) =>
        callback(
          (data?.prospects ?? [])
            .filter(({ firstAndLastName, firstName, lastName }) => firstAndLastName || firstName || lastName)
            .map(formatProspectOption)
        )
      )
      .catch(() => callback([]));
  }, 500);

  const debouncedLoadTrips = useDebouncedCallback((inputValue, callback) => {
    getToken()
      .then((token) =>
        ai.auth(token).get("/api/trips/search", {
          params: {
            q: inputValue,
            limit: DEFAULT_OPTIONS_MAX_LIMIT,
            prospectFilter: state.prospectOption?.value,
          },
        })
      )
      .then(({ data }) => callback((data?.data ?? []).map(formatTripOptionWithClient)))
      .catch(() => callback([]));
  }, 500);

  const handleSubmit = useCallback(async () => {
    const counterpart = (state.counterpart || "").trim();

    if (!state.tripOption?.value || !state.paymentType?.value || (config.counterpartEditable && !counterpart)) {
      return toast.error("Please fill in all required fields and retry.");
    }

    dispatch({ type: "fieldChanged", field: "isSaving", value: true });

    try {
      const token = await getToken();
      const provider = row?.objectType ? "stripe" : "xero";

      await ai.auth(token).post(`/api/${provider}/transactions/${row._id}/${config.endpoint}`, {
        tripId: state.tripOption.value,
        lineItemID: row?._lineItemID || null,
        paymentType: state.paymentType.value,
        notes: state.notes || "",
        ...(config.counterpartEditable ? { paidTo: counterpart } : {}),
      });

      toast.success(config.success);
      await onRequestClose(true);
    } catch (error) {
      Sentry.captureMessage(
        `${kind} link failed: ${error?.response?.status ?? "network"}`,
        "error"
      );
      toast.error(getApiErrorMessage(error, config.failure));
    } finally {
      dispatch({ type: "fieldChanged", field: "isSaving", value: false });
    }
  }, [config, getToken, kind, onRequestClose, row, state.counterpart, state.notes, state.paymentType, state.tripOption]);

  const isStripe = !!row?.objectType;
  const date = isStripe ? row?.chargedAt : row?.transactionDate;
  const signedAmount = row?._isLineItem
    ? ((row?.parentAmount ?? 0) < 0 ? -1 : 1) * Math.abs(row?.amount || 0)
    : row?.amount || 0;
  const amount = formatSignedAmount(signedAmount);

  return (
    <ReactModal
      isOpen={isOpen}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
      onRequestClose={handleClose}
      style={{
        overlay: { zIndex: 999999, backgroundColor: "rgb(32 53 68 / 50%)" },
        content: {
          width: "auto",
          border: "none",
          maxWidth: "700px",
          maxHeight: "85vh",
          minWidth: "calc(min(600px, 95vw))",
          borderRadius: "0",
          boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
        },
      }}
      appElement={document.getElementById("app")}
    >
      <div className="modal-header justify-content-center text-center">
        <h4>{config.title}</h4>
      </div>

      <div className="modal-body width-100 modal-body-with-scroll">
        <form className="w-100 base-form mt-10" onSubmit={(event) => event.preventDefault()}>
          <div className="form-group">
            <label className="base-label">Filter by Prospect</label>
            <AsyncSelect
              classNamePrefix="select"
              loadOptions={debouncedLoadProspects}
              placeholder="Search/Select Prospect"
              onChange={(option) => {
                dispatch({ type: "fieldChanged", field: "prospectOption", value: option });
                dispatch({ type: "fieldChanged", field: "tripOption", value: null });
              }}
              value={state.prospectOption}
              isClearable
              menuPosition="fixed"
            />
          </div>

          <div className="form-group">
            <label className="base-label">
              Filter by Trip <span className="required">*</span>
            </label>
            <AsyncSelect
              key={`passthrough-trip-${state.prospectOption?.value || "all"}`}
              classNamePrefix="select"
              loadOptions={debouncedLoadTrips}
              placeholder="Search/Select Trip"
              onChange={(option) => dispatch({ type: "fieldChanged", field: "tripOption", value: option })}
              value={state.tripOption}
              isClearable
              menuPosition="fixed"
            />
          </div>

          <div className="w-100 d-flex gap-20">
            <div className="form-group flex-half">
              <label className="base-label">
                {config.counterpartLabel}
                {config.counterpartEditable && <span className="required"> *</span>}
              </label>
              {config.counterpartEditable ? (
                <input
                  type="text"
                  maxLength={200}
                  value={state.counterpart}
                  onChange={(event) => dispatch({ type: "fieldChanged", field: "counterpart", value: event.target.value })}
                />
              ) : (
                <input type="text" value={state.counterpart || "—"} disabled readOnly />
              )}
            </div>

            <div className="form-group flex-half">
              <label className="base-label">
                Payment Type <span className="required">*</span>
              </label>
              <Select
                classNamePrefix="select"
                options={CLIENT_PAYMENT_TYPE_OPTIONS}
                placeholder="Payment Type"
                value={state.paymentType}
                onChange={(option) => dispatch({ type: "fieldChanged", field: "paymentType", value: option })}
                menuPosition="fixed"
              />
            </div>
          </div>

          <div className="w-100 d-flex gap-20">
            <div className="form-group flex-one">
              <label className="base-label">Date</label>
              <input type="text" value={date ? moment(date).format("M/D/YY") : "—"} disabled readOnly />
            </div>

            <div className="form-group flex-one">
              <label className="base-label">Transaction Type</label>
              <input type="text" value={signedAmount < 0 ? "Spent" : "Received"} disabled readOnly />
            </div>

            <div className="form-group flex-one">
              <label className="base-label">Amount</label>
              <input type="text" className={amount.className} value={amount.text} disabled readOnly />
            </div>
          </div>

          <TextAreaInput
            label="Notes"
            name="notes"
            containerClasses="mb-0"
            value={state.notes}
            handleChange={(event) => dispatch({ type: "fieldChanged", field: "notes", value: event.target.value })}
          />
        </form>
      </div>

      <div className="modal-footer no-border w-100 d-flex gap-10">
        <LoadingButton
          className="base-button evo-blue flex-half"
          onClick={handleSubmit}
          isLoading={state.isSaving}
          disabled={
            !state.tripOption?.value ||
            !state.paymentType?.value ||
            (config.counterpartEditable && !(state.counterpart || "").trim())
          }
        >
          Submit
        </LoadingButton>

        <button
          className="base-button btn-danger flex-half"
          onClick={handleClose}
          disabled={state.isSaving}
        >
          Cancel
        </button>
      </div>
    </ReactModal>
  );
};

export default TripRecordLinkModal;
